import { useTranslation } from 'react-i18next';

import { Font, Image, Link, StyleSheet, Text, View } from '@react-pdf/renderer';
import { IBusinessToolType } from 'constants/businessPlan';
import { PDFPage } from 'PDF/components/Page';
import { Chapter } from 'PDF/components/TableOfContents/Chapter';
import { WatermarkSvgComponent } from 'PDF/components/Watermark';
import { Lexend, Noto } from 'PDF/fonts';
import { SectionsAvailability } from 'PDF/types/pdfSections';

import { IOpeningBalanceSheets } from 'components/BusinessToolForm/Sections/FinancialPlan/OpeningBalanceSheets/utils/form';

import { getTableData, PrivateSectionsEnum } from './helpers';

Font.getRegisteredFonts();
Font.register(Lexend);
Font.register(Noto);

interface IProps {
  privateSections: { [key in PrivateSectionsEnum]?: boolean };
  businessPlanType: IBusinessToolType;
  sectionPicture?: string;
  sectionsAvailability: SectionsAvailability;
  openingBalanceSheetData?: IOpeningBalanceSheets;
  isStandardOrLightTariff: boolean;
  isExamplePage: boolean
}

export const TableOfContentsPdf = ({
  privateSections,
  businessPlanType,
  sectionPicture,
  sectionsAvailability,
  openingBalanceSheetData,
  isStandardOrLightTariff,
  isExamplePage
}: IProps) => {
  const tableData = getTableData(privateSections, businessPlanType, sectionsAvailability, isExamplePage, openingBalanceSheetData);
  const { t, i18n } = useTranslation();

  const currentLang = i18n?.resolvedLanguage || i18n?.language || '';
  const isCyrillicLang = /^(uk|ru|bg|sr|mk|kk|ky|tt|mn|uz|be|tg)/i.test(currentLang);
  const headingFontFamily = isCyrillicLang ? 'Noto' : 'Lexend';

  if (tableData.length === 0) return null;

  return (
    <PDFPage paddings={'36px 36px 42px 36px'} isStandardOrLightTariff={isStandardOrLightTariff}>
      <View id="tableOfContents" />
      <View style={styles.wrap}>
        <View>
          <Text fixed style={[styles.heading, { fontFamily: headingFontFamily }]}>
            {isExamplePage ? 'Table of contents' : t('chapters.TABLE_OF_CONTENTS')}
          </Text>
          {tableData.map((item) => (
            <Chapter key={item.chapterNumber} chapter={item} />
          ))}
          {!isStandardOrLightTariff && (
            <Link fixed href="https://growexa.com" style={styles.watermarkLink}>
              <WatermarkSvgComponent />
            </Link>
          )}
        </View>
        {sectionPicture && <Image src={sectionPicture} style={styles.image} fixed />}
      </View>
    </PDFPage>
  );
};

const styles = StyleSheet.create({
  heading: {
    fontFamily: 'Lexend',
    fontWeight: 500,
    fontSize: 25,
    color: '#212121',
    marginBottom: 32,
  },
  wrap: {
    display: 'flex',
    flexDirection: 'row',
  },
  image: {
    marginLeft: 'auto',
    maxHeight: '763px',
    maxWidth: '265px',
  },
  watermarkLink: {
    marginTop: 'auto',
    marginBottom: '-30px',
    alignSelf: 'flex-start',
  },
});
